import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DomText } from "../../../core/components/DomText";
import { useClue } from "../../../../game/contact3d/replicatedObjects/Clue";
import { Font } from "../../../core/components/TextElement";
import { getOwnerId } from "../../../../core/game/common/ReplicatedData";
import { Euler, Vector3 } from "three";
import { useUpdate } from "../../../core/util/UseUpdate";
import { addVectors, setVector, subVectors, vectorDown, vectorTimes, vectorUp } from "../../../core/util/SpatialUtils";
import { useGameState } from "../../../../game/contact3d/replicatedObjects/GameState";
import { useReplicatedObjectEvent } from "../../../../core/game/common/ReplicatedObjectView";
const BALL_GRAVITY = -12;
const TEXT_Y_OFFSET = vectorTimes(vectorUp(), 1);
const BUMP_BALL_SPEED = 5;
const DELETE_DELAY = 1.5;
const XZ_TILT_FORCE = 13;
export const ThreeJSClue = (props) => {
    const clue = useClue();
    const [savedClue, setSavedClue] = useState(undefined);
    const finalClue = useMemo(() => { return clue === undefined ? savedClue : clue; }, [clue, savedClue]);
    useEffect(() => {
        if (clue === undefined) {
            const timeout = setTimeout(() => {
                setSavedClue(undefined);
            }, DELETE_DELAY * 1000);
            return () => {
                clearTimeout(timeout);
            };
        }
        else {
            setSavedClue(clue);
        }
    }, [clue, setSavedClue]);
    return finalClue && _jsx(ThreeJSClueInternal, Object.assign({}, props, { clue: finalClue }));
};
const ThreeJSClueInternal = (props) => {
    var _a;
    const { playersWithApis, clue } = props;
    const textApiRef = useRef(undefined);
    const clueOwnerId = getOwnerId(clue);
    const { currentContactTime } = useGameState();
    const frameStateRef = useRef({
        targetOffset: new Vector3(0, 2, 0),
        playerVisual: (_a = playersWithApis[clueOwnerId]) === null || _a === void 0 ? void 0 : _a.apiRef.current,
        basePos: new Vector3(),
        horizontalSpeed: 5,
        speed: 0,
        bound: false,
        shouldDropBall: false,
        time: 0,
        direction: new Vector3(),
        targetPosInitialized: false,
    });
    const sphereObjectRef = useRef(null);
    const getPlayerPos = useCallback(() => {
        const frameState = frameStateRef.current;
        return frameState.playerVisual ? frameState.playerVisual.getPosition() : frameState.basePos;
    }, [frameStateRef]);
    useUpdate((state, deltaTime) => {
        var _a, _b, _c, _d;
        if (sphereObjectRef.current === null) {
            return;
        }
        const sphereObject = sphereObjectRef.current;
        const frameState = frameStateRef.current;
        if (frameState.playerVisual === undefined) {
            frameState.playerVisual = (_a = playersWithApis[clueOwnerId]) === null || _a === void 0 ? void 0 : _a.apiRef.current;
        }
        const targetPos = addVectors(getPlayerPos(), frameState.targetOffset);
        if (!frameState.targetPosInitialized) {
            setVector(sphereObject.position, new Vector3(targetPos.x, targetPos.y + 8, targetPos.z));
            frameState.targetPosInitialized = true;
        }
        if (!frameState.bound && (sphereObject.position.y > targetPos.y || frameState.speed > 0)) {
            frameState.speed += BALL_GRAVITY * deltaTime;
            sphereObject.position.addScaledVector(vectorUp(), frameState.speed * deltaTime);
            const horizontalDiff = subVectors(new Vector3(targetPos.x, 0, targetPos.z), new Vector3(sphereObject.position.x, 0, sphereObject.position.z));
            const horizontalDist = horizontalDiff.length();
            horizontalDiff.normalize();
            sphereObject.position.addScaledVector(horizontalDiff, Math.min(horizontalDist, frameState.horizontalSpeed * deltaTime));
            if (sphereObject.position.y <= targetPos.y && frameState.speed <= 0) {
                frameState.bound = true;
                setVector(sphereObject.position, targetPos);
                const oldSpeed = frameState.speed;
                frameState.speed = 0;
                if (frameState.shouldDropBall) {
                    (_b = frameState.playerVisual) === null || _b === void 0 ? void 0 : _b.applyXZTiltForce(frameState.direction.clone().multiplyScalar(XZ_TILT_FORCE));
                    dropBall(frameState, sphereObject);
                }
                else {
                    (_c = frameState.playerVisual) === null || _c === void 0 ? void 0 : _c.applyYForce(oldSpeed / 5);
                }
            }
        }
        else {
            if (sphereObject.position.distanceTo(targetPos) > 0.0001) {
                setVector(sphereObject.position, targetPos);
            }
        }
        (_d = textApiRef.current) === null || _d === void 0 ? void 0 : _d.setPosition(addVectors(sphereObject.position, TEXT_Y_OFFSET));
    });
    useReplicatedObjectEvent(clue.clueDeniedEvent, () => {
        if (sphereObjectRef.current === null) {
            return;
        }
        smackBall(frameStateRef.current, sphereObjectRef.current);
    });
    useReplicatedObjectEvent(clue.clueContactEvent, () => {
        var _a, _b, _c;
        if (sphereObjectRef.current === null) {
            return;
        }
        throwBall((_b = (_a = playersWithApis[clue.submittedAnswer.sourceId]) === null || _a === void 0 ? void 0 : _a.apiRef) === null || _b === void 0 ? void 0 : _b.current, currentContactTime, ((_c = clue.submittedAnswer) === null || _c === void 0 ? void 0 : _c.answer) === clue.clueAnswer, frameStateRef.current, sphereObjectRef.current);
    });
    useReplicatedObjectEvent(clue.additionalHintEvent, () => {
        if (sphereObjectRef.current === null) {
            return;
        }
        bumpBall(frameStateRef.current, sphereObjectRef.current, getPlayerPos);
    });
    return _jsxs(_Fragment, { children: [_jsxs("mesh", { scale: 0.5, ref: sphereObjectRef, children: [_jsx("sphereGeometry", {}), _jsx("meshPhongMaterial", { color: 0x41c5d9 })] }), _jsx(DomText, { apiRef: textApiRef, text: clue.hints[clue.hints.length - 1], font: Font.Inter, fontSize: 220 })] });
};
function dropBall(frameState, sphereObject) {
    frameState.basePos = addVectors(sphereObject.position, vectorTimes(frameState.direction, -50), new Vector3(0, -4, 0));
    frameState.playerVisual = null;
    const newTime = frameState.time / 2;
    frameState.horizontalSpeed = 3 / newTime;
    frameState.shouldDropBall = false;
    frameState.speed = 1;
    unstickBall(frameState);
}
function throwBall(otherVisual, time, successful, frameState, sphereObject) {
    if (otherVisual === undefined || otherVisual === null) {
        return;
    }
    frameState.shouldDropBall = !successful;
    frameState.time = time;
    const otherVisualPosition = otherVisual.getPosition();
    const horizontalDiff = subVectors(new Vector3(otherVisualPosition.x, 0, otherVisualPosition.z), new Vector3(sphereObject.position.x, 0, sphereObject.position.z));
    const horizontalDist = horizontalDiff.length();
    frameState.direction = horizontalDiff.clone();
    frameState.direction.normalize();
    if (frameState.shouldDropBall) {
        frameState.targetOffset.addScaledVector(frameState.direction, -0.25);
        frameState.targetOffset.addScaledVector(vectorDown(), 2);
    }
    const horizontalSpeed = horizontalDist / time;
    const verticalSpeed = time * Math.abs(BALL_GRAVITY) / 2 * (frameState.shouldDropBall ? 0.8 : 1);
    frameState.playerVisual = otherVisual;
    frameState.speed = verticalSpeed;
    frameState.horizontalSpeed = horizontalSpeed;
    unstickBall(frameState);
}
function bumpBall(frameState, sphereObject, getPlayerPos) {
    const targetPos = addVectors(getPlayerPos(), frameState.targetOffset);
    if (sphereObject.position.y <= targetPos.y) {
        frameState.speed = BUMP_BALL_SPEED;
        unstickBall(frameState);
    }
}
function smackBall(frameState, sphereObject) {
    const randomAngle = Math.random() * 2 * Math.PI;
    const randomVector = new Vector3(1, 0, 0).applyEuler(new Euler(0, randomAngle, 0));
    frameState.basePos = addVectors(sphereObject.position, vectorTimes(randomVector, 1000), new Vector3(0, -10, 0));
    frameState.playerVisual = null;
    frameState.horizontalSpeed = 12;
    frameState.shouldDropBall = false;
    frameState.speed += 7;
    unstickBall(frameState);
}
function unstickBall(frameState) {
    frameState.bound = false;
}
//# sourceMappingURL=ThreeJSClue.js.map